<script setup lang="ts">
import { ref, watch, nextTick, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEditions } from '@/composables/useEditions'
import { useUnsavedChanges } from '@/composables/useUnsavedChanges'
import MarkdownEditor from '@/components/common/MarkdownEditor.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'

function isAxiosError(e: unknown): e is { response: { status: number; data: { errors?: Record<string, string[]>; message?: string } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

const { t } = useI18n()
const route = useRoute()
const { edition, fetchEdition, updateEdition } = useEditions()
const { markDirty, markClean } = useUnsavedChanges()

const eventSlug = route.params['eventSlug'] as string
const editionSlug = route.params['editionSlug'] as string

const formLoading = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref('')
const successMessage = ref('')
const raceRules = ref<string>('')
const initialized = ref(false)

/**
 * The edition venue id, snapshotted once at mount.
 *
 * The API exposes the venue only as a nested `venue` relation - there is no
 * flat `venue_id` on an edition payload. The update endpoint does not
 * eager-load that relation either, so the edition object left behind by a
 * previous save carries no `venue` key at all. This panel resends the whole
 * edition payload on every save, and `venue_id` is now required server-side,
 * so it reads the id once from a freshly fetched edition instead of trusting
 * the shared edition state at save time.
 */
const venueId = ref<number | null>(null)

const charCount = computed(() => raceRules.value.length)
const isOverLimit = computed(() => charCount.value > 50000)

watch(raceRules, () => {
  if (initialized.value) markDirty(route.name as string)
})

onMounted(async () => {
  // Refetch when the cached edition belongs to another edition, or when it
  // carries no `venue` key at all - which is exactly what an update response
  // looks like. The show endpoint always eager-loads the venue, so after this
  // guard `edition.venue` is present (possibly null for a legacy edition).
  if (
    !edition.value ||
    edition.value.slug !== editionSlug ||
    edition.value.venue === undefined
  ) {
    await fetchEdition(eventSlug, editionSlug)
  }
  if (edition.value) {
    raceRules.value = edition.value.race_rules ?? ''
    venueId.value = edition.value.venue?.id ?? null
  }
  await nextTick()
  initialized.value = true
})

async function handleSave(): Promise<void> {
  errors.value = {}
  generalError.value = ''
  successMessage.value = ''
  formLoading.value = true

  try {
    if (!edition.value) return

    await updateEdition(eventSlug, editionSlug, {
      name: edition.value.name,
      start_date: edition.value.start_date,
      end_date: edition.value.end_date,
      venue_id: venueId.value,
      race_rules: raceRules.value,
    })

    markClean(route.name as string)
    successMessage.value = t('editions.race_rules.save_success')
  } catch (e: unknown) {
    if (isAxiosError(e)) {
      if (e.response.status === 422 && e.response.data.errors) {
        errors.value = e.response.data.errors
      } else {
        generalError.value = t('editions.form.error_generic')
      }
    } else {
      generalError.value = t('editions.form.error_generic')
    }
  } finally {
    formLoading.value = false
  }
}
</script>

<template>
  <div>
    <div
      v-if="successMessage"
      class="mb-6 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 text-sm text-emerald-400"
    >
      {{ successMessage }}
    </div>

    <div
      v-if="generalError"
      class="mb-6 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
    >
      {{ generalError }}
    </div>

    <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
      <div class="mb-6">
        <h2 class="text-lg font-semibold text-white">{{ t('editions.race_rules.heading') }}</h2>
        <p class="text-sm text-slate-400 mt-1">{{ t('editions.race_rules.description') }}</p>
      </div>

      <div class="space-y-3">
        <MarkdownEditor
          v-model="raceRules"
          :placeholder="t('editions.race_rules.placeholder')"
          :max-length="50000"
          :disabled="formLoading"
          input-id="race-rules-editor"
        />

        <div
          class="flex justify-end text-xs"
          :class="isOverLimit ? 'text-red-400' : 'text-slate-500'"
        >
          {{ t('editions.race_rules.char_count', { count: charCount }) }}
        </div>

        <div
          v-if="errors['race_rules']"
          class="text-sm text-red-400"
        >
          {{ errors['race_rules'][0] }}
        </div>

        <!--
          The edition venue is mandatory, and this panel resends the whole
          edition payload. A legacy edition with no venue therefore fails here
          with a venue_id error that has no field on this screen: point the
          organizer at the tab that does have the picker.
        -->
        <div
          v-if="errors['venue_id']"
          class="text-sm text-red-400"
        >
          {{ t('editions.form.venue_missing_error') }}
        </div>
      </div>

      <div class="mt-8 flex justify-end">
        <button
          type="button"
          :disabled="formLoading || isOverLimit"
          class="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          @click="handleSave"
        >
          <AppSpinner v-if="formLoading" size="button" />
          {{ t('editions.race_rules.save_button') }}
        </button>
      </div>
    </div>
  </div>
</template>
